import { action, observable, makeObservable } from 'mobx';
import rootStore from 'stores/index';
import { AuthMethod } from 'constants/AuthMethod';
import { destroy, get, post } from 'stores/_services/dataService';
import { Role } from 'constants/Role';

interface IRoleMapping {
    role: {
        name: string;
    };
}

interface IUserPermission {
    resourceType: string;
    resourceId: string | null | undefined;
}

interface IUpdatePassword {
    currentPassword: string;
    newPassword: string;
    email?: string;
    oAuthId?: string;
}

export interface ITenants {
    _id: string;
    name: string;
    description: string;
}

export interface Metadata {
    itcToken: string | undefined;
    itcOrgId: string | undefined;
    itcUserId: string | undefined;
    isAuthwebSecurityAdmin: string | undefined;
    companySsoEnabled: string | undefined;
    userSsoEnabled: string | undefined;
}

export interface INewUser {
    id?: string;
    email: string;
    username: string;
    role: string;
    password: string;
    confirmPassword: string;
    roleMapId?: string;
    userPermissions: IUserPermission[];
}

export interface IUser {
    id?: string;
    email: string;
    role?: string;
    username: string;
    enabled: boolean;
    roleMappings: IRoleMapping[];
    userPermissions: IUserPermission[];
    admin: boolean;
    isAdmin: boolean;
    infocyteUser: boolean;
    oAuthId?: string;
    password?: string;
    hashedEmail: string;
    excludeK1Enforcement?: boolean | null;
    isExternalAnalystUser: boolean;
}

export default class UserStore {
    public user: IUser = {
        id: '',
        email: '',
        username: '',
        oAuthId: '',
        enabled: true,
        roleMappings: [],
        userPermissions: [],
        admin: false,
        isAdmin: false,
        infocyteUser: false,
        isExternalAnalystUser: false
    };

    constructor(data?: IUser) {
        makeObservable(this, {
            user: observable,
            update: action
        });

        if (data) {
            this.update(data);
        }
    }

    update(user: IUser): void {
        const roleMapping = (user.roleMappings && user.roleMappings[0]) || {};
        const role = roleMapping.role || {};
        const isAdmin = role.name === Role.ADMIN;
        const isExternalAnalystUser = role.name === Role.EXTERNAL_ANALYST;

        this.user = {
            ...this.user,
            ...user,
            role: role.name,
            isAdmin,
            isExternalAnalystUser,
            infocyteUser: !!user.email.match(/@(infocyte|datto).com/)
        };
    }

    async delete(): Promise<void> {
        await destroy(`users/${this.user.id}`);
    }

    async reloadMetadata(): Promise<void> {
        await get('users/me/reloadMetadata');
    }

    async updatePassword(currentPassword: string, newPassword: string): Promise<void> {
        const settings = await rootStore.authStore.getAuthenticationSettings();

        let route = '';
        const payload: IUpdatePassword = { currentPassword, newPassword };

        if (settings?.authenticationMethod === AuthMethod.LOCAL) {
            route = 'users/me/password';
        }

        const res = await post(route, payload);
        const { data } = res;

        if (Object.hasOwnProperty.call(data, 'success') && data.success === false) {
            throw new Error(data.message);
        }
    }
}
